import React, { useState } from 'react';
import { Box, DialogActions, DialogContent, DialogTitle, Alert } from '@mui/material';
import useAuth from 'auth/UseAuth';
import { useForm } from 'react-hook-form';
import { CancelButton } from 'components/CancelButton';
import { PrimaryButton } from 'components/PrimaryButton';
import { DialogCloseButton } from 'components/DialogCloseButton';
import { DialogOpenButton } from 'components/DialogOpenButton';
import { ErrorIndicator } from 'components/ErrorIndicator';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { WarningIndicator } from 'components/WarningIndicator';
import { ErrorManagement, LoadingState } from '../../components/LoadingStateUtil';
import { SuperDuperFiestaEntity, IssueDispositionConfig, WhitelistIssues } from 'data/SuperDuperFiestaData';
import {
  PatientApprovalPatientMeasurement,
  PatientMeasurementIssueUpdate,
  UpdatePatientMeasurementIssues,
} from 'data/PatientApprovalData';
import { FormTextField } from 'components/form/FormTextField';
import { PersistentDialog } from '../../components/PersistentDialog';

interface FormValues {
  description: string;
}

const defaultFormValues: FormValues = {
  description: '',
};

export interface RowIssues {
  rowId: string;
  issueIds: number[];
  pipelineId: string;
}

interface WhitelistIssuesModalProps {
  selectedEntity: SuperDuperFiestaEntity | null;
  canWhitelist: boolean;
  rowIssues: RowIssues[];
  open?: boolean;
  onClose?: () => void;
  patientMeasurementUpdate?: {
    researchProjectId: string;
    deidentity: string;
    measurementData: PatientMeasurementIssueUpdate;
    onMeasurementsUpdated: (measurements: PatientApprovalPatientMeasurement[]) => void;
  };
}

export const WhitelistIssuesModal = ({
  selectedEntity,
  canWhitelist,
  rowIssues,
  open: externalOpen = false,
  onClose,
  patientMeasurementUpdate,
}: WhitelistIssuesModalProps) => {
  const { accessToken } = useAuth();
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });
  const [internalOpen, setInternalOpen] = useState(false);

  const isOpen = externalOpen || internalOpen;

  const form = useForm<FormValues>({
    defaultValues: defaultFormValues,
    mode: 'onChange',
  });

  const { reset, control, watch, trigger } = form;

  const handleOpen = async () => {
    setInternalOpen(true);
  };

  const handleSave = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    ErrorManagement('Loading', setLoadingState, async () => {
      if (!selectedEntity) {
        throw new Error('No entity selected');
      }

      const { description } = watch();

      if (patientMeasurementUpdate) {
        const { measurementData, onMeasurementsUpdated } = patientMeasurementUpdate;
        measurementData.issues = [];
        const measurements = await UpdatePatientMeasurementIssues(
          patientMeasurementUpdate.researchProjectId,
          patientMeasurementUpdate.deidentity,
          measurementData,
          accessToken
        );
        if (measurements) {
          onMeasurementsUpdated(measurements);
        }
      }

      const whitelistConfig: IssueDispositionConfig[] = rowIssues.flatMap(({ rowId, issueIds, pipelineId }) =>
        issueIds.map(issueId => ({
          rowId,
          issueId,
          pipelineId,
          description: description.trim(),
        }))
      );

      await WhitelistIssues({ entityId: selectedEntity.entityId }, whitelistConfig, accessToken);
      handleClose();
    });
  };

  const handleClose = () => {
    reset(defaultFormValues);
    setInternalOpen(false);
    setLoadingState({ status: 'NotStarted' });
    onClose?.();
  };

  return (
    <>
      {canWhitelist && <DialogOpenButton title='Whitelist' onClick={handleOpen} disabled={rowIssues.length === 0} />}
      <PersistentDialog open={isOpen} onClose={handleClose} fullWidth maxWidth='sm'>
        <DialogTitle>
          <DialogCloseButton onClick={handleClose} />
          Whitelist Issues
        </DialogTitle>
        <DialogContent>
          <Box component='form' sx={{ flexGrow: 1, mt: 1 }}>
            <Alert severity='info' sx={{ mb: 2 }}>
              Whitelist all issues for the selected rows
            </Alert>
            <FormTextField
              name='description'
              control={control}
              label='Enter a description for the whitelisting issues'
              trigger={trigger}
              additionalProps={{ multiline: true, rows: 6 }}
            />
          </Box>
        </DialogContent>
        <LoadingIndicator loadingState={loadingState} margin={'LR'} />
        <ErrorIndicator loadingState={loadingState} />
        <WarningIndicator loadingState={loadingState} />
        <DialogActions>
          <CancelButton onClick={handleClose} disabled={loadingState.status === 'Loading'}>
            Cancel
          </CancelButton>
          <PrimaryButton onClick={handleSave} disabled={loadingState.status === 'Loading'}>
            Save
          </PrimaryButton>
        </DialogActions>
      </PersistentDialog>
    </>
  );
};
